// src/components/ToastContainer.tsx
import React from 'react';
import { Toast, ToastType } from '../types';
import { XIcon, CheckCircleIcon, AlertTriangleIcon } from './icons';
import { getRoddoTheme } from '../constants/theme';

const InfoIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

type ToastVisualConfig = {
  icon: React.FC<{ className?: string }>;
  accent: string;
  glow: string;
  label: string;
};

const getToastConfig = (type?: ToastType): ToastVisualConfig => {
  switch (type) {
    case 'success':
      return {
        icon: CheckCircleIcon,
        accent: '#66D6A9',
        glow: 'rgba(102,214,169,0.22)',
        label: 'Clean Cast',
      };
    case 'error':
      return {
        icon: AlertTriangleIcon,
        accent: '#EF6A73',
        glow: 'rgba(239,106,115,0.22)',
        label: 'Snagged',
      };
    case 'warning':
      return {
        icon: AlertTriangleIcon,
        accent: '#F2B84B',
        glow: 'rgba(242,184,75,0.22)',
        label: 'Heads Up',
      };
    case 'info':
    default:
      return {
        icon: InfoIcon,
        accent: '#2F80ED',
        glow: 'rgba(47,128,237,0.14)',
        label: 'Trail Note',
      };
  }
};

const resolveDarkMode = (explicit?: boolean): boolean => {
  if (typeof explicit === 'boolean') return explicit;
  if (typeof document === 'undefined') return false;

  try {
    return document.documentElement.classList.contains('dark');
  } catch {
    return false;
  }
};

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
  isDarkMode?: boolean;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onDismiss, isDarkMode }) => {
  const dark = resolveDarkMode(isDarkMode);
  const theme = getRoddoTheme(dark);
  const config = getToastConfig(toast.type as ToastType);
  const { icon: Icon, accent, label } = config;

  return (
    <div
      className="roddo-toast-pop roddo-emotion-card animate-fade-in flex w-full items-center justify-between gap-3 rounded-[14px] border p-3 transition-transform active:scale-[0.99]"
      style={{
        background: dark ? '#101827' : '#FFFCF7',
        color: theme.text,
        borderColor: theme.borderStrong,
        boxShadow: dark
          ? '0 4px 12px rgba(0,0,0,0.15)'
          : '0 4px 12px rgba(89,65,37,0.07)',
              }}
      role="status"
      aria-live="polite"
    >
      <div className="flex min-w-0 items-center gap-3">
        <div
          className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-[10px] border"
          style={{
            background: dark ? 'var(--roddo-panel-dark)' : 'var(--roddo-panel-light)',
            borderColor: dark ? 'var(--roddo-border-dark)' : 'var(--roddo-border-light)',
            color: accent,
            boxShadow: `inset 0 1px 0 ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.55)'}`,
          }}
        >
          <Icon className="h-5 w-5" />
        </div>

        <div className="min-w-0">
          <div
            className="mb-0.5 text-[10px] font-black uppercase tracking-[0.16em]"
            style={{ color: accent }}
          >
            {label}
          </div>
          <p
            className="line-clamp-2 text-sm font-semibold"
            style={{ color: theme.text }}
            title={toast.message}
          >
            {toast.message}
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-[10px] border transition-all hover:-translate-y-0.5 active:translate-y-px active:scale-95"
        style={{
          borderColor: dark ? 'var(--roddo-border-dark)' : 'var(--roddo-border-light)',
          background: dark ? 'var(--roddo-panel-dark)' : 'var(--roddo-panel-light)',
          color: theme.textSoft,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = dark
            ? 'var(--roddo-panel-dark-strong)'
            : 'var(--roddo-panel-light-strong)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = dark
            ? 'var(--roddo-panel-dark)'
            : 'var(--roddo-panel-light)';
        }}
        aria-label="Dismiss toast"
      >
        <XIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

interface ToastContainerProps {
  toasts: Toast[];
  onDismiss: (id: string) => void;
  isDarkMode?: boolean;
}

const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  onDismiss,
  isDarkMode,
}) => {
  if (!toasts || toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed left-1/2 z-[2000] flex w-full max-w-sm -translate-x-1/2 flex-col gap-2 px-4"
      style={{ bottom: 'calc(var(--roddo-nav-h, 72px) + env(safe-area-inset-bottom) + 18px)' }}>
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem toast={toast} onDismiss={onDismiss} isDarkMode={isDarkMode} />
        </div>
      ))}
    </div>
  );
};

export default ToastContainer;